import React, { useState } from 'react';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { REMOVE_URL, UPDATE_URL } from './CveActions';
import { scopeText, targetText } from './driftShared';

const list = (v) => (Array.isArray(v) ? v : []);
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const shownName = (p) => (p.id && p.displayName ? p.displayName : p.name);
const programKey = (p) => `${p.name}|${p.version}`;
const VERB = { update: 'Update', remove: 'Remove' };

// One machine's worst programs on the CVE report, with the way to take care of them: tick programs, choose to update or remove, and put the jobs
// in the machine's queue as a test (dry run) or for real after a confirmation. Like the By application view, the page sends the program (its catalog
// id, or its name when it has none), the kind and the machine id, never a script. Only a program with a catalog id can be updated.
// onQueue(url, body) -> the API's answer {queued, refused, failed}. One request is made for each ticked program.
export default function CveMachineActions({ account, item, programs, onQueue }) {
  const [action, setAction] = useState('update');
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const name = item.results?.computerName || item.deviceId;
  const verb = VERB[action];
  const can = (p) => action === 'remove' || Boolean(p.id);
  const choosable = programs.filter(can);
  const chosen = choosable.filter((p) => selected[programKey(p)]);
  const allChosen = choosable.length > 0 && chosen.length === choosable.length;

  const queue = async (kind) => {
    setBusy(true);
    setOutcome(null);
    const answers = [];
    try {
      for (const p of chosen) {
        // Update names the program (its catalog id); Remove names this one copy by the name and version the machine reported, so an old copy
        // can go while a newer one of the same program stays.
        const program = action === 'update' ? { wingetId: p.id } : { name: p.name, version: p.version, exact: true };
        // eslint-disable-next-line no-await-in-loop
        const result = await onQueue(action === 'update' ? UPDATE_URL : REMOVE_URL, { account, kind, deviceIds: [item.deviceId], ...program });
        answers.push({ program: shownName(p), result });
      }
      setOutcome({ kind, answers });
      setSelected({});
    } catch (ex) {
      setOutcome({ kind, error: errorText(ex), answers });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const queued = (outcome?.answers || []).reduce((n, a) => n + list(a.result?.queued).length, 0);
  const problems = (outcome?.answers || []).flatMap((a) => [
    ...list(a.result?.refused).map((r) => `Not queued, ${a.program}: ${r.error}`),
    ...list(a.result?.failed).map((r) => `Failed, ${a.program}: ${r.error}`),
  ]);

  return (
    <div>
      <div className="v2-tools">
        <label className="v2-check">
          <input type="checkbox" aria-label={`Select all programs of ${name}`} checked={allChosen} disabled={choosable.length === 0 || busy}
            onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(choosable.map((p) => [programKey(p), true])) : {})} />
          {`Select all (${choosable.length})`}
        </label>
        <select className="v2-select" aria-label="What to do" value={action} disabled={busy}
          onChange={(e) => { setAction(e.target.value); setSelected({}); }}>
          <option value="update">Update</option>
          <option value="remove">Remove</option>
        </select>
        <button type="button" className="v2-btn" disabled={chosen.length === 0 || busy} onClick={() => queue('check')}>{`Test (${chosen.length})`}</button>
        <button type="button" className="v2-btn primary" disabled={chosen.length === 0 || busy} onClick={() => setConfirming(true)}>{`Apply (${chosen.length})`}</button>
        {busy && <span className="v2-spin" role="status" aria-label="Queueing" />}
      </div>
      {outcome?.error && <div className="v2-alert err" role="alert">{outcome.error}</div>}
      {outcome && !outcome.error && (
        <div className={`v2-alert${problems.length ? ' info' : ''}`} role="status">
          {`${queued} ${outcome.kind === 'check' ? 'test' : 'apply'} ${queued === 1 ? 'job' : 'jobs'} in the queue.`}
          {problems.map((text) => <div className="v2-sub" key={text}>{text}</div>)}
        </div>
      )}
      <div className="v2-items">
        {programs.map((p) => (
          <div className="v2-app-machine" key={programKey(p)}>
            <div>
              <input type="checkbox" aria-label={`Select ${shownName(p)}`} disabled={busy || !can(p)}
                title={can(p) ? '' : 'It has no catalog id, so it can only be removed.'}
                checked={Boolean(selected[programKey(p)])} onChange={(e) => setSelected({ ...selected, [programKey(p)]: e.target.checked })} />
            </div>
            <div className="name">{shownName(p)}</div>
            <div>
              {action === 'update' ? targetText(p.version, p.available) : (p.version || '')}
              {scopeText(p.scope) && <div className="v2-sub">{scopeText(p.scope)}</div>}
            </div>
            <div>{`Critical ${num(p.critical)}, High ${num(p.high)}`}<div className="v2-sub">{`Worst score ${num(p.highestScore)}`}</div></div>
            <div>
              {p.unused && <div>Not used lately</div>}
              {!can(p) && <div className="v2-sub">No catalog id: it can be removed but not updated here.</div>}
            </div>
          </div>
        ))}
      </div>

      {confirming && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label={`${verb} on ${name}`}>
            <h2>{`${verb} ${chosen.length} ${chosen.length === 1 ? 'program' : 'programs'} on ${name}?`}</h2>
            <p>{`A job for each will be put in the queue of this machine and run for real: it will ${action === 'remove' ? 'remove that copy of the program' : 'update the program'}.`}</p>
            {chosen.map((p) => <div key={programKey(p)}>{shownName(p)}</div>)}
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={() => queue('apply')} disabled={busy}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
